import React, { useState, useEffect } from 'react';
import { getRequest } from '../../utils/axios';
import { NotificationManager } from 'react-notifications';

import MainA from '../../layout/Main Admin';
import Table from '../../components/atoms/Table';
import Input from '../../components/atoms/Input';
import Loader from '../../components/atoms/Loader';
import CustomDropdown from '../../components/atoms/CustomDropdown';
import Modal from '../../components/atoms/Modal';
import Button from '../../components/atoms/Button';

import userIcon from '../../assets/icons/user.svg';

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash
} from "@fortawesome/free-solid-svg-icons";

import './styles.scss';
import moment from 'moment';

const PikuUsers = () => {
  const [users, setUsers] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortColumn, setSortColumn] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const imageUrl = "https://dynamicproductstorage.blob.core.windows.net/profile/";

  const statusOptions = [
    { label: 'All', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      let resp = await getRequest('PikuUser/GetAllUsers');
      if (resp?.status === 200 && Array.isArray(resp.data)) {
        setUsers(resp.data);

        const sortedData = [...resp.data].sort((a, b) => {
          return new Date(b.lastUpdatedDateTime) - new Date(a.lastUpdatedDateTime);
        });
        setFilteredData(filterAndSearch(sortedData, searchTerm, filterStatus));
      } 
    } catch (err) {
      NotificationManager.error('Failed to fetch Piku users.');
      console.error(err);
    } finally {
      setLoading(false);
    }
    };

  const filterAndSearch = (list = [], search = '', status = 'all') => {
    const value = (search || '').toString().toLowerCase();
    let filtered = Array.isArray(list) ? [...list] : [];

    if (value.trim() !== '') {
      filtered = filtered.filter((u) => {
        const name = ((u.userFirstName || '') + ' ' + (u.userLastName || '')).toString().toLowerCase();
        const email = (u.userEmail || '').toString().toLowerCase();
        const phone = (u.phoneNo || '').toString().toLowerCase();
        return name.includes(value) || email.includes(value) || phone.includes(value);
      });
    }

    if (status === 'active') filtered = filtered.filter((u) => u.isActive);
    if (status === 'inactive') filtered = filtered.filter((u) => !u.isActive);

    return filtered;
  };

  const handleSearch = (e) => {
    const value = (e.target.value || '').toLowerCase();
    setSearchTerm(value);
    setFilteredData(filterAndSearch(users, value, filterStatus));
  };

  const handleSort = (column) => {
    const order = sortColumn === column && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortColumn(column);
    setSortOrder(order);

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = (a[column] || '').toString().toLowerCase();
      const bValue = (b[column] || '').toString().toLowerCase();
      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredData(sorted);
  };

  const handleDelete = (e, user) => {
    e.preventDefault();
    setUserToDelete(user);
    setDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setUserToDelete(null);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;
    const userId = userToDelete.userId ;
    if (!userId) return;

    setLoading(true);
    try {
      const resp = await getRequest(`PikuUser/Delete/${userId}`);
      if (resp?.status === 200) {
        const updatedUsers = users.filter((u) => (u.userId) !== userId);
        setUsers(updatedUsers);
        setFilteredData(filterAndSearch(updatedUsers, searchTerm, filterStatus));
        NotificationManager.success('User removed');
        closeDeleteModal();
      } else {
        NotificationManager.error('Failed to remove user');
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error('Failed to remove user');
    } finally {
      setLoading(false);
      setUserToDelete(null);
      setDeleteModalOpen(false);
    }
  };
  const handleStatusSelect = (option) => {
    const val = option?.value || 'all';
    setFilterStatus(val);
    setFilteredData(filterAndSearch(users, searchTerm, val));
  };

  const handleToggleActive = async (e, user) => {
    e.preventDefault();
    const userId = user.userId ;
    if (!userId) return;

    const newStatus = !user.isActive;
    setLoading(true);
    try {
      const resp = await getRequest(`PikuUser/ChangeStatus/${userId}/${newStatus}`);
      if (resp?.status === 200) {
        const updatedUsers = users.map((u) => (u === user ? { ...u, isActive: newStatus } : u));
        setUsers(updatedUsers);
        setFilteredData(filterAndSearch(updatedUsers, searchTerm, filterStatus));
        NotificationManager.success('Status updated');
      } else {
        NotificationManager.error('Failed to update status');
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error('Failed to update status');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainA className="b-pikuusers">
      <div className="event-header">
        <div className="event-name">
          <h1>Piku Users</h1>
        </div>

        <div className="candidate-buttons">
          <Input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
          <CustomDropdown
            options={statusOptions}
            value={statusOptions.find((o) => o.value === filterStatus)}
            onSelect={handleStatusSelect}
            placeholder="Status"
            className="custom-dropdown-variant"
          />
        </div>
      </div>

      <Modal isOpen={deleteModalOpen} closeModal={closeDeleteModal} className="delete-modal">
        <div className="circle">!</div>
        <h4>Are you sure you want to remove this user?</h4>
        <div className='modal-buttons'>
          <Button onClick={confirmDelete}>Yes, Remove it</Button>
          <Button onClick={closeDeleteModal} type="primary">Cancel</Button>
        </div>
      </Modal>

      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="pikuusers-table">
          <Table pagination={true} pageSize={10} totalRows={filteredData.length} categoryName="users">
            <thead>
              <tr>
                <th></th>
                <th onClick={() => handleSort('userFirstName')}>Name {sortColumn === 'userFirstName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => handleSort('userEmail')}>Email {sortColumn === 'userEmail' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => handleSort('phoneNo')}>Phone No {sortColumn === 'phoneNo' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                <th onClick={() => handleSort('lastLogin')}>Last Login {sortColumn === 'lastLogin' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.length > 0 ? (
                filteredData.map((u, idx) => (
                  <tr key={idx}>
                    <td className="user-img-td"><img src={u.profileImage ? imageUrl + u.profileImage : userIcon} className='user-img' alt="user" /></td>
                    <td>{u.userFirstName + ' ' + u.userLastName}</td>
                    <td>{u.userEmail}</td>
                    <td>{u.phoneNo}</td>
                    <td>{u.lastLoginDateTime ? moment(u.lastLoginDateTime).format('MMM DD, YYYY HH:mm:ss') : '-'}</td>
                    <td><Input type="checkbox" checked={u.isActive} onChange={(e) => handleToggleActive(e, u)} /></td>
                    <td>
                      <FontAwesomeIcon icon={faTrash} className="delete-icon" onClick={(e) => handleDelete(e, u)} />
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center' }}>No user(s) found.</td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      )}
    </MainA>
  );
};

export default PikuUsers;
